import { useLingui } from '@lingui/react/macro';
import { useRef, useState, type RefObject } from 'react';
import { withForm } from '@worldcoo/ui/components/form';
import { readAcceptedHtml } from '@worldcoo/ui/lib/consent';
import { cn } from '@worldcoo/ui/lib/utils';
import { usePersistedStore } from '@/lib/app-store';
import { TermsAndConditionsDialog } from '../../../-components/terms-and-conditions';
import { TermsCheckboxField } from '../terms-checkbox-field';
import { detailsFormOpts } from './details-form.opts';
import { TelefonicaTermsAndConditions } from './telefonica-terms';

type AcceptedTerms = { checked: boolean; text: string; label: string };

function recordTermsOnToggle(props: {
	checked: boolean;
	stored: AcceptedTerms | undefined;
	textElement: HTMLElement | null;
	labelElement: HTMLElement | null;
	setContent: (content: AcceptedTerms | undefined) => void;
}) {
	const capturedByDialogAccept = props.checked && props.stored?.text && props.stored?.label;
	if (capturedByDialogAccept) return;

	const text = readAcceptedHtml(props.textElement);
	if (props.checked && props.labelElement && text !== undefined) {
		props.setContent({ checked: true, label: props.labelElement.innerHTML, text });
	} else if (!props.checked) {
		props.setContent(undefined);
	}
}

/** Worldcoo's data-protection notice, shown in its dialog and kept as a hidden copy to record from. */
function WorldcooDataProtection(props: { ref: RefObject<HTMLTableElement | null> }) {
	const { t } = useLingui();
	return (
		<TermsAndConditionsDialog
			ref={props.ref}
			responsible={t`WORLDCOO S.L.`}
			purpose={t`Manage the business relationship in all its areas, as well as your donation and your certificate request, and the sending of commercial communications by electronic means.`}
			legitimacy={t`The legal basis for processing your data is obtaining your consent.`}
			recipients={t`Data will not be transferred to third parties unless there is a legal obligation to do so.`}
			rights={t`You have the right to access, rectify and delete data, as well as other rights, as explained in the additional information.`}
			additionalInfo={t`You can consult additional and detailed information about Data Protection on our page <a href="https://www.worldcoo.com/privacy-policy">web</a> or by contacting <a href="mailto:rgpd@worldcoo.com">rgpd@worldcoo.com</a>.`}
		/>
	);
}

/**
 * The terms the donor accepts, each recorded with the exact text shown, plus hidden copies to record from when
 * a box is ticked without opening its dialog.
 */
export const DonationTerms = withForm({
	...detailsFormOpts,
	props: {} as {
		portalId: string;
		ngoNames: string;
		authorName: string;
	},
	render: function DonationTerms({ form, portalId, ngoNames, authorName }) {
		const { t } = useLingui();
		const setTermsContent = usePersistedStore((state) => state.setTermsContent);
		const setNgosTermsContent = usePersistedStore((state) => state.setNgosTermsContent);
		const setTelefonicaTermsContent = usePersistedStore((state) => state.setTelefonicaTermsContent);

		const [openTermsDialog, setOpenTermsDialog] = useState(false);
		const [openNgosTermsDialog, setOpenNgosTermsDialog] = useState(false);
		const [openTelefonicaTermsDialog, setOpenTelefonicaTermsDialog] = useState(false);

		const termsLabelRef = useRef<HTMLDivElement>(null);
		const termsDialogRef = useRef<HTMLDivElement>(null);
		const termsTextRef = useRef<HTMLTableElement>(null);
		const hiddenTermsTextRef = useRef<HTMLTableElement>(null);
		const ngosTermsLabelRef = useRef<HTMLDivElement>(null);
		const ngosTermsDialogRef = useRef<HTMLDivElement>(null);
		const ngosTermsTextRef = useRef<HTMLTableElement>(null);
		const hiddenNgosTermsTextRef = useRef<HTMLTableElement>(null);
		const telefonicaTermsLabelRef = useRef<HTMLDivElement>(null);
		const telefonicaTermsDialogRef = useRef<HTMLDivElement>(null);
		const telefonicaTermsTextRef = useRef<HTMLDivElement>(null);
		const hiddenTelefonicaTermsTextRef = useRef<HTMLDivElement>(null);

		return (
			<>
				<div className="flex flex-col gap-5">
					<form.AppField
						name="termsAndConditions"
						listeners={{
							onChange: ({ value }) =>
								recordTermsOnToggle({
									checked: value,
									stored: usePersistedStore.getState().termsContent,
									textElement: termsTextRef.current ?? hiddenTermsTextRef.current,
									labelElement: termsLabelRef.current,
									setContent: setTermsContent,
								}),
						}}
						children={(field) => (
							<TermsCheckboxField
								field={field}
								labelText={t`I have read, understand and accept the terms of `}
								linkText={(() => {
									const name = 'Worldcoo';
									return t`Data Protection of ${name}`;
								})()}
								dialogTitle={t`Data Protection Terms`}
								acceptButtonText={t`I have read, understand and accept`}
								open={openTermsDialog}
								onOpenChange={setOpenTermsDialog}
								labelRef={termsLabelRef}
								dialogRef={termsDialogRef}
								textRef={termsTextRef}
								termsContent={<WorldcooDataProtection ref={termsTextRef} />}
								onAccept={() => {
									const text = readAcceptedHtml(termsTextRef.current);
									if (text === undefined || !termsLabelRef.current) return;

									form.setFieldValue('termsAndConditions', true);
									setTermsContent({
										checked: true,
										label: termsLabelRef.current.innerHTML,
										text,
									});
									setOpenTermsDialog(false);
								}}
							/>
						)}
					/>

					<form.AppField
						validators={{ onChangeListenTo: ['certificate'] }}
						name="ngosTermsAndConditions"
						listeners={{
							onChange: ({ value }) =>
								recordTermsOnToggle({
									checked: value,
									stored: usePersistedStore.getState().ngosTermsContent,
									textElement: ngosTermsTextRef.current ?? hiddenNgosTermsTextRef.current,
									labelElement: ngosTermsLabelRef.current,
									setContent: setNgosTermsContent,
								}),
						}}
						children={(field) => (
							<form.Subscribe
								selector={(state) => state.values.certificate}
								children={(certificate) => (
									<div className={cn(!certificate && 'hidden')}>
										<TermsCheckboxField
											field={field}
											labelText={t`I have read, understand and accept the terms of `}
											linkText={t`Data Protection of ${ngoNames}`}
											dialogTitle={t`Data Protection Terms`}
											acceptButtonText={t`I have read, understand and accept`}
											open={openNgosTermsDialog}
											onOpenChange={setOpenNgosTermsDialog}
											labelRef={ngosTermsLabelRef}
											dialogRef={ngosTermsDialogRef}
											textRef={ngosTermsTextRef}
											termsContent={
												<TermsAndConditionsDialog
													ref={ngosTermsTextRef}
													responsible={ngoNames}
													purpose={t`Fundraising for NGO projects, keeping you informed about the NGO and the evolution of the project for which you have donated funds and issuing tax deduction certificates.`}
													legitimacy={t`The legal basis for processing your data is obtaining your consent.`}
													recipients={t`Data will not be transferred to third parties unless there is a legal obligation to do so.`}
													rights={t`You have the right to access, rectify and delete data, as well as other rights, as explained in the additional information.`}
													additionalInfo={t`You can consult the additional and detailed information about Data Protection on each NGO's website.\n\n<b>${ngoNames}</b>`}
												/>
											}
											onAccept={() => {
												const text = readAcceptedHtml(ngosTermsTextRef.current);
												if (text === undefined || !ngosTermsLabelRef.current) return;

												form.setFieldValue('ngosTermsAndConditions', true);
												setNgosTermsContent({
													checked: true,
													label: ngosTermsLabelRef.current.innerHTML,
													text,
												});
												setOpenNgosTermsDialog(false);
											}}
										/>
									</div>
								)}
							/>
						)}
					/>

					{portalId === 'telefonica' && (
						<form.AppField
							name="telefonicaTermsAndConditions"
							listeners={{
								onChange: ({ value }) =>
									recordTermsOnToggle({
										checked: value,
										stored: usePersistedStore.getState().telefonicaTermsContent,
										textElement: telefonicaTermsTextRef.current ?? hiddenTelefonicaTermsTextRef.current,
										labelElement: telefonicaTermsLabelRef.current,
										setContent: setTelefonicaTermsContent,
									}),
							}}
							children={(field) => (
								<TermsCheckboxField
									field={field}
									labelText={t`I have read, understand and accept the terms of `}
									linkText={t`Data Protection of ${authorName}`}
									dialogTitle={t`Data Protection Terms`}
									acceptButtonText={t`I have read, understand and accept`}
									open={openTelefonicaTermsDialog}
									onOpenChange={setOpenTelefonicaTermsDialog}
									labelRef={telefonicaTermsLabelRef}
									dialogRef={telefonicaTermsDialogRef}
									textRef={telefonicaTermsTextRef}
									hideUnresetTailwind
									termsContent={<TelefonicaTermsAndConditions ref={telefonicaTermsTextRef} />}
									onAccept={() => {
										const text = readAcceptedHtml(telefonicaTermsTextRef.current);
										if (text === undefined || !telefonicaTermsLabelRef.current) return;

										form.setFieldValue('telefonicaTermsAndConditions', true);
										setTelefonicaTermsContent({
											checked: true,
											label: telefonicaTermsLabelRef.current.innerHTML,
											text,
										});
										setOpenTelefonicaTermsDialog(false);
									}}
								/>
							)}
						/>
					)}
				</div>
				<div className="hidden">
					<WorldcooDataProtection ref={hiddenTermsTextRef} />
					<form.Subscribe
						selector={(state) => state.values.certificate}
						children={(certificate) =>
							certificate ? (
								<TermsAndConditionsDialog
									ref={hiddenNgosTermsTextRef}
									responsible={ngoNames}
									purpose={t`Fundraising for NGO projects, keeping you informed about the NGO and the evolution of the project for which you have donated funds and issuing tax deduction certificates.`}
									legitimacy={t`The legal basis for processing your data is obtaining your consent.`}
									recipients={t`Data will not be transferred to third parties unless there is a legal obligation to do so.`}
									rights={t`You have the right to access, rectify and delete data, as well as other rights, as explained in the additional information.`}
									additionalInfo={t`You can consult the additional and detailed information about Data Protection on each NGO's website.\n\n<b>${ngoNames}</b>`}
								/>
							) : null
						}
					/>
					{portalId === 'telefonica' && <TelefonicaTermsAndConditions ref={hiddenTelefonicaTermsTextRef} />}
				</div>
			</>
		);
	},
});
